import { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import moment from 'moment'
import { startCase } from 'lodash'
import { faLock, faBoltAuto } from '@fortawesome/pro-regular-svg-icons'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { centsToDollars } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { fetchSingleTransactionRule } from '@/actions/admin/transactionRuleActions'
import {
  getCurrentUser,
  getFinancialProfile,
  selectIsInUnservicedFreeTrial,
} from '@/selectors/user.selectors'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'

import {
  formatAccountNameWithInstitution,
  getTaxCompliantExplanation,
} from '../helpers'
import { updateUserTransaction } from '../transactions.slice'
import { selectFinancialAccountById } from '@/src/selectors/financeSelectors'
import { getTransactionCategoryById } from '@/features/reports/reports.selectors'
import { selectTransactionRuleById } from '../transactionRules.selectors'
import { useAnalyticsTrack } from '@/features/amplitude'

import { TypographyStyles, TextColors } from '@/styles'
import {
  Text,
  Button,
  Icon,
  Card,
  Link,
  Alert,
  CardColors,
  Separator,
  Tooltip,
  Select,
  DatePicker,
} from '@/ui'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { TransactionCategoryLabel } from '@/components/TransactionCategoryLabel'
import { TransactionInput } from './NicknameInput'
import { CategorizationTooltip } from './CategorizationTooltip'
import { DetailsTooltip } from './DetailsTooltip'
import { DetailsAlert } from './DetailsAlert'
import { TransactionAmountInput } from './AmountInput'
import { TRANSACTION_TYPE_OPTIONS } from '@/constants/transactionConstants'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'

interface DetailsPanelProps {
  transaction: TransactionOrSplit
  splitIndex?: number
  totalSplits?: number
  isManualTransaction?: boolean
  setPreventClose: (preventClose: boolean) => void
}

export const DetailsPanel = ({
  transaction,
  splitIndex,
  totalSplits,
  isManualTransaction,
  setPreventClose,
}: DetailsPanelProps) => {
  const dispatch = useAppDispatch()

  const {
    id,
    description,
    date,
    amountInCents,
    financialAccountId,
    autocategorizedTransactionRuleId,
    manuallyCreated,
    reconciliationId,
  } = transaction
  const financialProfile = useReselector(getFinancialProfile)
  const currentUser = useReselector(getCurrentUser)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const [transactionType, setTransactionType] = useState<
    TransactionRuleCategoryType | undefined
  >(transaction.type as TransactionRuleCategoryType)
  const [updating, setUpdating] = useState(false)
  const taxCompliantExplanation = useMemo(() => {
    return getTaxCompliantExplanation(
      transaction.transactionCategory?.identifier,
      financialProfile?.taxEntityType
    )
  }, [
    transaction.transactionCategory?.identifier,
    financialProfile?.taxEntityType,
  ])

  useEffect(() => {
    if (autocategorizedTransactionRuleId) {
      dispatch(fetchSingleTransactionRule(autocategorizedTransactionRuleId))
    }
  }, [autocategorizedTransactionRuleId, dispatch])

  const autocategorizationRule = useReselector(
    selectTransactionRuleById,
    Number(autocategorizedTransactionRuleId)
  )

  const account = useReselector(
    selectFinancialAccountById,
    Number(financialAccountId)
  )

  const autoCategory = useReselector(
    getTransactionCategoryById,
    autocategorizationRule?.transactionCategoryId
  )

  const transactionCategory = useReselector(
    getTransactionCategoryById,
    transaction.transactionCategoryId
  )

  const track = useAnalyticsTrack()

  const updateTransactionType = async (
    value: TransactionRuleCategoryType | undefined
  ) => {
    try {
      setTransactionType(value)
      setUpdating(true)

      const updated = await updateUserTransaction(transaction.id, {
        type: value,
        excludedAt: value === 'personal' ? Date.now() : null,
      })(dispatch)

      if (updated) {
        setUpdating(false)
      }
    } catch (_error) {
      setUpdating(false)
    } finally {
      setPreventClose(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {Boolean(reconciliationId) && (
        <Alert
          variant="warning"
          customIconName={faLock}
          description="Your bookkeeping team has reconciled this transaction. Its amount and date are locked. You can still change its category or add a note."
        />
      )}

      <div className="flex-1 mt-2">
        <DetailsAlert
          isManualTransaction={Boolean(manuallyCreated)}
          isUserManualTransaction={Boolean(isManualTransaction)}
          transactionId={id}
          isReconciled={Boolean(reconciliationId)}
        />
      </div>

      {isManualTransaction && (
        <div className="flex-1 w-full">
          <Text variant={TypographyStyles.EYEBROW}> Description </Text>
          <TransactionInput transaction={transaction} nickname={false} />
        </div>
      )}

      {!isManualTransaction && (
        <div className="flex-1 gap-2">
          <Text variant={TypographyStyles.H3} color={TextColors.MUTED}>
            {' '}
            Description{' '}
          </Text>

          <Text variant={TypographyStyles.BODY_SM} className="mt-2">
            {' '}
            {description}{' '}
          </Text>
        </div>
      )}

      {typeof splitIndex !== 'number' && (
        <div className="flex-1 gap-2">
          <div className="flex flex-row items-center">
            <Text variant={TypographyStyles.H3} color={TextColors.MUTED}>
              {' '}
              Nickname{' '}
            </Text>

            <DetailsTooltip label="Nickname">
              <Text variant={TypographyStyles.BODY_SM}>
                You can give this transaction a nickname to easily identify it.
                This is for your reference and has no impact on your
                bookkeeping.
              </Text>
            </DetailsTooltip>
          </div>

          <TransactionInput transaction={transaction} />
        </div>
      )}

      <Separator />

      <div className="flex-1 flex items-center">
        <Text
          variant={TypographyStyles.H3}
          className="w-1/3 mt-2"
          color={TextColors.MUTED}
        >
          Date
        </Text>

        <div className="w-2/3">
          {isManualTransaction ? (
            <DatePicker
              style={{ width: '100%' }}
              value={DateTime.fromISO(transaction.date, {
                zone: 'utc',
              }).toFormat(DATE_FORMATS_LUXON.INPUT)}
              maxDate={new Date()}
              disabled={Boolean(reconciliationId)}
              onChange={(value) => {
                updateUserTransaction(transaction.id, { date: value })(dispatch)
              }}
            />
          ) : (
            <Text variant={TypographyStyles.BODY_SM} className="mt-2 text-left">
              {moment.utc(date).format(DATE_FORMATS.DISPLAY_SHORT)}
            </Text>
          )}
        </div>
      </div>
      <div className="flex-1 flex items-center">
        <Text
          variant={TypographyStyles.H3}
          className="mt-2 w-1/3"
          color={TextColors.MUTED}
        >
          Amount
        </Text>
        {isManualTransaction && (
          <div className="w-2/3">
            <Text variant={TypographyStyles.BODY_SM} className="mt-2 text-left">
              <TransactionAmountInput
                transaction={transaction}
                disabled={Boolean(reconciliationId)}
              />
            </Text>
          </div>
        )}
        {!isManualTransaction && (
          <div className="w-2/3">
            <Text variant={TypographyStyles.BODY_SM} className="mt-2 text-left">
              <CurrencyFormatLabel value={centsToDollars(amountInCents)} />
            </Text>
          </div>
        )}
      </div>
      <div className="flex-1 flex items-center">
        <Text
          variant={TypographyStyles.H3}
          className="mt-2 w-1/3"
          color={TextColors.MUTED}
        >
          Account
        </Text>
        <Text
          variant={TypographyStyles.BODY_SM}
          className="w-2/3 mt-2 text-left"
        >
          {' '}
          {formatAccountNameWithInstitution(account)}{' '}
        </Text>
      </div>
      <div className="flex-1 flex items-center">
        <Text
          variant={TypographyStyles.H3}
          className="mt-2 w-1/3"
          color={TextColors.MUTED}
        >
          Type
        </Text>
        <div className="w-2/3 text-left float-left">
          <Select
            position="item-aligned"
            value={transactionType ? transactionType : undefined}
            disabled={updating}
            onValueChange={(value) => {
              setPreventClose(true)
              updateTransactionType(value as TransactionRuleCategoryType)
            }}
            placeholder="Select Type"
            options={TRANSACTION_TYPE_OPTIONS}
          />

          {typeof splitIndex === 'number' && (
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              Split Transaction ({splitIndex + 1}/{totalSplits})
            </Text>
          )}
        </div>
      </div>
      <div className="flex-1 flex items-center">
        <div className="flex flex-row items-center mt-2 w-1/3">
          <Text variant={TypographyStyles.H3} color={TextColors.MUTED}>
            Category
          </Text>
          <CategorizationTooltip />
        </div>
        <div className="w-2/3 text-left float-left">
          <TransactionCategoryLabel
            variant="text"
            useRecategorizationModal
            filtered
            transaction={transaction}
            editable
          />
          {transaction.type !== 'personal' && (
            <>
              {taxCompliantExplanation}
              {taxCompliantExplanation &&
                transactionCategory?.isTaxCompliant && (
                  <Link
                    variant="secondary"
                    className="text-neutral-700 mt-4 block text-sm"
                    href="https://support.joinheard.com/hc/en-us/articles/13615864394263"
                    newPage
                  >
                    Common mix-ups with tax-compliant categories
                  </Link>
                )}
            </>
          )}
        </div>
      </div>

      <Separator />

      <div className="flex-1">
        <div className="flex flex-row items-center mb-2">
          <Text variant={TypographyStyles.H3} color={TextColors.MUTED}>
            Auto-Categorization Rules
          </Text>
          <DetailsTooltip
            label="Auto-categorization rules"
            track={() => track('clicked autocat rule tooltip')}
          >
            <Text>
              Instead of manually categorizing transactions that are always
              categorized the same way, the Heard app does it automatically. We
              call this process &quot;auto-categorization&quot;.
            </Text>
          </DetailsTooltip>
        </div>

        {autocategorizationRule ? (
          <Card color={CardColors.PRIORITY}>
            <div className="flex flex-col">
              <div className="flex items-center">
                <div className="w-1/8">
                  <Icon icon={faBoltAuto} color="bg-moss-500" />
                </div>
                <div className="w-7/8">
                  <Text variant={TypographyStyles.H3}>
                    &quot;{autocategorizationRule.ruleText}&quot;
                  </Text>
                  <Text>
                    Type:{' '}
                    {startCase(
                      autocategorizationRule.transactionCategoryType ||
                        undefined
                    )}
                  </Text>
                  <Text>Category: {autoCategory?.name}</Text>
                  <Text variant={TypographyStyles.H3} color={TextColors.GREEN}>
                    H
                    <Tooltip>
                      <Tooltip.Trigger>
                        <Button
                          variant="link"
                          className="inline-block ml-2 font-normal"
                        >
                          Created By Heard
                        </Button>
                      </Tooltip.Trigger>
                      <Tooltip.Content>
                        <div className="flex flex-col">
                          <Text
                            variant={TypographyStyles.H3}
                            className="mb-3 w-full"
                          >
                            Heard-created rules
                          </Text>
                          <Text
                            variant={TypographyStyles.BODY_SM}
                            className="w-full"
                            color={TextColors.MUTED}
                          >
                            We apply certain auto-categorization rules to all
                            customers to help categorize recurring transactions
                            consistently.
                          </Text>
                        </div>
                      </Tooltip.Content>
                    </Tooltip>
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <>
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              None applied.
            </Text>
            {!isInUnservicedFreeTrial && (
              <Card color={CardColors.LEVEL_1}>
                <Text variant={TypographyStyles.BODY_SM}>
                  If this is a recurring transaction, we can categorize it
                  automatically for you.{' '}
                  <Link
                    href={`https://${process.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/hc/en-us/requests/new?tf_subject=Auto-categorization%20Rule%20Request%20-%20${encodeURIComponent(
                      description
                    )}%20APP&tf_description=${encodeURIComponent(
                      `
                            <ul>
                              <li><strong>Automatically categorize recurring transactions with this description:</strong> ${description} APP</li>
                              <li><strong>Categorize as:</strong> Requested category</li>
                              <li><strong>Additional comments:</strong> Please share any explanation, context, or comments here</li>
                            </ul>
                          `
                    )}&${process.env.VITE_ZENDESK_FIELD_ID}=topic_area__bookkeeping&tf_anonymous_requester_email=${encodeURIComponent(
                      currentUser?.email || ''
                    )}`}
                    trackMessage="clicked submit autocat request"
                    newPage
                  >
                    Submit a request
                  </Link>
                </Text>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  )
}
